import { useCallback, useEffect, useState } from "react";
import {
  getTrainingGpuWorkspaceUsage,
  type TrainingGpuClusterUsage,
  type TrainingGpuWorkspaceUsage,
} from "@/api/trainingGpu";
import type { LoadState } from "@/components/dataset/loadState";
import { gpuUsageWindow, useGpuClusterUsage, type GpuUsagePeriod } from "@/components/gpu/usage";
import { createLogger } from "@/logging";

const logger = createLogger("page/dataDashboard");

/**
 * 대시보드의 GPU 사용량 기간. 이 페이지에는 기간 컨트롤이 없다. 그래서 기간을 하나로 고정한다.
 * 쿼터 탭의 URL 기본값과 따로 정한다.
 */
export const DASHBOARD_GPU_USAGE_PERIOD: GpuUsagePeriod = "30d";

export type DashboardGpuUsageController = {
  usage: LoadState<TrainingGpuWorkspaceUsage>;
  cluster: LoadState<TrainingGpuClusterUsage>;
  isLoading: boolean;
  reload: () => void;
};

/**
 * GPU 사용량 요약 띠의 두 조회를 실행한다. workspace 사용량은 이 hook 이 읽고, 클러스터 점유는
 * 공용 hook 이 읽는다. 두 조회는 같은 기간과 같은 `reload` 를 쓴다.
 *
 * 두 조회의 실패는 서로 독립이다. 페이지가 실패를 조회마다 따로 알린다. 그래서 workspace 사용량도
 * 상태(`LoadState`)로 반환한다.
 *
 * 다시 조회하는 동안 직전 값을 유지한다. 새로고침할 때 타일이 비었다가 다시 채워지지 않는다.
 */
export function useDashboardGpuUsage(): DashboardGpuUsageController {
  const [usage, setUsage] = useState<LoadState<TrainingGpuWorkspaceUsage>>({ status: "idle" });
  const [isUsageLoading, setIsUsageLoading] = useState(true);
  const [reloadToken, setReloadToken] = useState(0);
  const { state: cluster, isLoading: isClusterLoading } = useGpuClusterUsage(
    DASHBOARD_GPU_USAGE_PERIOD,
    reloadToken,
  );

  const reload = useCallback(() => {
    setIsUsageLoading(true);
    setReloadToken((token) => token + 1);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    async function load(): Promise<void> {
      setIsUsageLoading(true);
      setUsage((previous) => (previous.status === "success" ? previous : { status: "loading" }));
      try {
        const data = await getTrainingGpuWorkspaceUsage(
          gpuUsageWindow(DASHBOARD_GPU_USAGE_PERIOD, Date.now()),
          { signal },
        );
        if (!signal.aborted) {
          setUsage({ status: "success", data });
        }
      } catch (error) {
        if (signal.aborted) return;
        logger.error("Failed to load workspace GPU usage.", error);
        setUsage({
          status: "error",
          message: "기간 GPU-hours와 사용분 비용을 불러오지 못했습니다.",
        });
      } finally {
        if (!signal.aborted) {
          setIsUsageLoading(false);
        }
      }
    }
    void load();
    return () => {
      controller.abort();
    };
  }, [reloadToken]);

  return { usage, cluster, isLoading: isUsageLoading || isClusterLoading, reload };
}
